/**
 * Hours-vs-target status bucketing, shared by the approvals inbox table and the
 * approver detail drawer so the two surfaces never disagree about what colour a
 * given week is.
 *
 * Bands are relative to the person's own `targetMinutes` (users can have
 * per-user weekly targets), not a hardcoded 40h week.
 */

/** Named states so call sites never pass raw colour strings around. */
export type HoursBucket = 'zero' | 'target' | 'over' | 'under';

/** Hours logged past target before a week counts as over. Two hours. */
export const HOURS_TOLERANCE_MINUTES = 120;

/**
 * Classify a week's total against its target.
 *
 * - `zero`   — nothing logged
 * - `target` — at target, or over it but within tolerance
 * - `over`   — past target by more than the tolerance
 * - `under`  — some hours logged, but short of target
 */
export function hoursBucket(
  totalMinutes: number,
  targetMinutes: number,
  toleranceMinutes: number = HOURS_TOLERANCE_MINUTES,
): HoursBucket {
  if (totalMinutes === 0) return 'zero';
  if (totalMinutes > targetMinutes + toleranceMinutes) return 'over';
  if (totalMinutes >= targetMinutes) return 'target';
  return 'under';
}

/** CSS class for a bucket, e.g. `hours--over`. */
export function hoursBucketClass(bucket: HoursBucket): string {
  return `hours--${bucket}`;
}
